import { STEPS } from '../constants/steps';

/**
 * Component to display steps from both sides in a consolidated view
 */
export default class CrossSideSteps {
    /**
     * Create a new CrossSideSteps instance
     * 
     * @param {HTMLElement} container - Container element 
     * @param {object} stepManager - Reference to the StepManager
     */
    constructor(container, stepManager) {
        this.container = container;
        this.stepManager = stepManager;
        this.isVisible = false;
        
        this.initialize();
    }
    
    /**
     * Initialize component and create DOM structure
     */
    initialize() {
        // Create container element
        this.element = document.createElement('div');
        this.element.className = 'stc-cross-side-steps';
        this.element.style.display = 'none'; // Hidden by default
        
        // Create columns for each side
        this.element.innerHTML = `
            <div class="stc-cross-side-column">
                <div class="stc-cross-side-title">
                    Front Side <span class="stc-side-indicator stc-side-front">Front</span>
                </div>
                <div class="stc-cross-side-steps-list front-steps"></div>
            </div>
            <div class="stc-cross-side-column">
                <div class="stc-cross-side-title">
                    Back Side <span class="stc-side-indicator stc-side-back">Back</span>
                </div>
                <div class="stc-cross-side-steps-list back-steps"></div>
            </div>
        `;
        
        // Add to container
        this.container.insertBefore(this.element, this.container.firstChild);
        
        // Get list containers
        this.frontList = this.element.querySelector('.front-steps');
        this.backList = this.element.querySelector('.back-steps');
        
        // Bind events to step manager
        this.bindEvents();
    }
    
    /**
     * Bind to step manager events
     */
    bindEvents() {
        // Update on step changes
        this.stepManager.on('stepComplete', () => this.update());
        this.stepManager.on('stepIncomplete', () => this.update());
        this.stepManager.on('stepDataChange', () => this.update());
        
        // Update when side changes
        this.stepManager.on('sideChange', () => this.update());
    }
    
    /**
     * Show the cross-side overview
     */
    show() {
        this.isVisible = true;
        this.element.style.display = '';
        this.update();
    }
    
    /**
     * Hide the cross-side overview
     */
    hide() {
        this.isVisible = false;
        this.element.style.display = 'none';
    }
    
    /**
     * Toggle visibility
     * 
     * @returns {boolean} New visibility state
     */
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
        return this.isVisible;
    }
    
    /**
     * Update the display with current step status
     */
    update() {
        if (!this.isVisible) return;
        
        // Get completed steps
        const completedSteps = {
            front: Array.from(this.stepManager.completedSteps.front),
            back: Array.from(this.stepManager.completedSteps.back)
        };
        
        // Update front side steps
        this.updateSideSteps('front', completedSteps.front);
        
        // Update back side steps
        this.updateSideSteps('back', completedSteps.back);
    }
    
    /**
     * Update steps for a specific side
     * 
     * @param {string} side - 'front' or 'back'
     * @param {array} completedStepIds - Array of completed step IDs
     */
    updateSideSteps(side, completedStepIds) {
        const listEl = side === 'front' ? this.frontList : this.backList;
        const steps = STEPS[side];
        
        // Create HTML for steps
        let html = '';
        
        steps.forEach(step => {
            const isCompleted = completedStepIds.includes(step.id);
            const stepData = this.stepManager.getStepDataForSide(side, step.id);
            const isEnabled = !stepData || !('enabled' in stepData) || stepData.enabled;
            const hasPrice = step.price && isEnabled;
            
            html += `
                <div class="stc-cross-side-step" data-step-id="${step.id}">
                    <div class="stc-cross-side-step-status ${isCompleted ? 'completed' : 'incomplete'}"></div>
                    <span>${step.title}</span>
                    ${hasPrice && isCompleted ? `<span class="stc-step-price">(+$${step.price.toFixed(2)})</span>` : ''}
                </div>
            `;
        });
        
        // Update the list
        listEl.innerHTML = html;
    }
}